import './i18n';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector'; // Assuming LanguageSelector is in components
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaUpload, FaCheck, FaTimes, FaSpinner, FaDownload } from "react-icons/fa";
import "./App.css";
import pleasantSound from './sounds/pleasant.mp3';
import unpleasantSound from './sounds/unpleasant.mp3';
import Header from './components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero section component
const Hero = () => {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 to-black/70 z-10"></div>
      <div className="hero-slider">
        <div className="hero-slide" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1730757679771-b53e798846cf')" }}></div>
        <div className="hero-slide" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1619134778706-7015533a6150')" }}></div>
        <div className="hero-slide" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1698612059734-280fed56f417')" }}></div>
      </div>
      <div className="relative z-20 container mx-auto px-6 py-32 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          {t('Welcome to Members Area')}
        </h1>
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          {t('verify_title')}
        </h1>
        <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto">
          {t('verify_description')}
        </p>
        <button
          onClick={() => document.getElementById('upload-section').scrollIntoView({ behavior: 'smooth' })}
          className="bg-gold text-black font-bold py-4 px-8 rounded-lg hover:bg-gold/80 transition duration-300 text-lg"
        >
          {t('authenticate_now')}
        </button>
      </div>
    </div>
  );
};

// Brand logos component
const BrandLogos = () => {
  const { t } = useTranslation();
  const logos = [
    { name: "Rolex", image: "https://images.unsplash.com/photo-1706801803974-fc52c9aaac6c" },
    { name: "Omega", image: "https://images.unsplash.com/photo-1573048338291-70f6fe5bfe25" },
    { name: t("Covers most Luxury Brands like Patek Philippe, Audemars Piguet, Richard Mille, Jaeger-LeCoultre, A. Lange & Söhne, Cartier, most Japanese and many other well known Brands..."), image: "https://images.unsplash.com/photo-1588599037715-98324a65c8fb" }
  ];
  return (
    <div className="bg-gray-100 py-16">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-12">{t('trusted_by_watch_enthusiasts')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {logos.map((logo, index) => (
            <div key={index} className="brand-logo-card">
              <div className="h-32 w-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-gold flex items-center justify-center bg-white">
                <img
                  src={logo.image}
                  alt={logo.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-center font-semibold text-xl">{logo.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const cameraInstructions = [
  'Take a good picture of "Case"',
  'Take a good picture of "Bexel" if any',
  'Take a good picture of "Crown" (The small knob on the side used to set time/date and wind the watch)',
  'Take a good picture of "Strap / Bracelet"',
  'Take a good picture of "Dial" (The main “face” of the watch showing the time)',
  'Take a good picture of "Sub-dials" (if any)',
  'Take a good picture of "Date Window"',
  'Take a good picture of "Caseback" (The rear of the watch case)',
  'Take a good picture of Clasp from outside',
  'Take a good picture of Clasp from inside',
];

// File upload component
const UploadSection = ({ onAnalysisComplete }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stageText, setStageText] = useState('');
  const videoRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [isFrontCamera, setIsFrontCamera] = useState(false);
  const [uploads, setUploads] = useState({ images: [], video: null });
  const [cameraMode, setCameraMode] = useState(false); // true if using camera capture flow
  const [cameraStep, setCameraStep] = useState(0); // 0-9 for images, 10 for video
  const [capturedImages, setCapturedImages] = useState([]); // for camera flow
  const [capturedVideo, setCapturedVideo] = useState(null);
  const [videoRecording, setVideoRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const videoRecordRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' (back) or 'user' (front)
  const [previewCapture, setPreviewCapture] = useState(null); // preview for retake

  const startCamera = (useFrontCamera = false) => {
    setCameraActive(true);
    const constraints = {
      video: {
        facingMode: useFrontCamera ? 'user' : 'environment',
      },
    };
    navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setTimeout(() => {
          window.scrollBy({ top: 300, behavior: 'smooth' });
        }, 500);
      })
      .catch((err) => {
        console.error("Error accessing camera:", err);
        setCameraActive(false);
      });
  };

  const toggleCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    const newCameraMode = !isFrontCamera;
    setIsFrontCamera(newCameraMode);
    startCamera(newCameraMode);
  };

  const capturePhoto = async () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      const file = new File([blob], 'captured-image.jpg', { type: 'image/jpeg' });
      setFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      setCameraActive(false);
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    });
  };

  const handleUpload = async () => {
    const validImages = uploads.images.filter((image) => image !== null); // Filter out null values
    if (validImages.length === 0) {
      setUploadError(t("please_select_image"));
      return;
    }
    setIsUploading(true);
    setUploadError(null);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        const newProgress = prev + 1;
        const stageIndex = Math.floor((newProgress / 100) * stages.length);
        setStageText(stages[stageIndex] || '');
        return newProgress;
      });
    }, Math.random() * (300 - 250) + 250);
    try {
      const formData = new FormData();
      validImages.forEach((image) => {
        formData.append('files', image);
      });
      if (uploads.video) {
        formData.append('video', uploads.video);
      }
      const response = await axios.post(`${API}/analyze-watch`, formData, {
        headers:
        {
          'Content-Type': 'multipart/form-data'
        }
      });
      onAnalysisComplete(response.data);
      document.getElementById('results-section').scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error("Error uploading image:", error);
      let errorMessage = t("failed_to_analyze");
      if (error.response) {
        if (error.response.status === 400 && error.response.data.detail === "Multiple Image Error!") {
          errorMessage = "Multiple Image Error!";
        } else if (error.response.status === 500) {
          errorMessage = t("server_error_analyzing");
        }
      }
      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };
  const clearUpload = () => {
    setFile(null);
    setPreview(null);
    setUploadError(null);
  };
  const clearAndRetake = () => {
    setFile(null);
    setPreview(null);
    setUploadError(null);
    startCamera();
  };

  // Camera flow: start camera and scroll, clear uploads
  const startCameraFlow = async () => {
    setCameraMode(true);
    setCameraStep(0);
    setCapturedImages([]);
    setCapturedVideo(null);
    setUploads({ images: [], video: null }); // clear local uploads
    setCameraFacingMode('environment'); // always start with back camera
    setTimeout(() => {
      document.getElementById('camera-capture-section').scrollIntoView({ behavior: 'smooth' });
    }, 200);
    // Start camera
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setMediaStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      setCameraMode(false);
      alert('Unable to access camera.');
    }
  };

  // Toggle camera (front/back)
  const handleToggleCamera = async () => {
    // Stop current stream
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    const newMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
    setCameraFacingMode(newMode);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: newMode } });
      setMediaStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      alert('Unable to access camera.');
    }
  };

  // Camera capture for images with retake support
  const handleCameraCapture = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const file = new File([blob], `camera-capture-${cameraStep + 1}.jpg`, { type: 'image/jpeg' });
      setPreviewCapture({ file, url: URL.createObjectURL(file) });
      setTimeout(() => {
        const previewImg = document.getElementById('camera-photo-preview');
        if (previewImg) {
          previewImg.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }, 'image/jpeg');
  };
  // Accept previewed capture
  const acceptPreviewCapture = () => {
    setCapturedImages((prev) => {
      const updated = [...prev];
      updated[cameraStep] = previewCapture.file;
      return updated;
    });
    setPreviewCapture(null);
    if (cameraStep === 9) {
      // After 10th photo, stop camera
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }
    }
    setCameraStep((prev) => prev + 1);
  };
  // Retake (just clear preview)
  const retakePreviewCapture = () => {
    setPreviewCapture(null);
  };

  // When camera flow is done, update uploads state
  useEffect(() => {
    if (capturedImages.length === 10 && capturedVideo) {
      setUploads({ images: capturedImages, video: capturedVideo });
      setCameraMode(false);
      setCameraStep(0);
      setCapturedImages([]);
      setCapturedVideo(null);
    }
  }, [capturedImages, capturedVideo]);

  // Adjusted spacing for "Browse Pic" buttons and added numbering from 1 to 11
  return (
    <div id="upload-section" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-2">{t('upload_title')}</h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          {t('Take clear photos of your watch from various angles for the most accurate authentication results.')}
        </p>
        <div className="max-w-2xl mx-auto">
          {/* Camera Button */}
          <div className="flex justify-center mb-8">
            <button
              className="bg-blue-700 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-800 transition mr-4"
              onClick={startCameraFlow}
            >
              Use Your Camera
            </button>
          </div>
          {/* Camera Capture Flow */}
          {cameraMode && (
            <div id="camera-capture-section" className="mb-10 p-6 bg-white rounded-lg shadow-lg flex flex-col items-center">
              <button
                className="mb-4 bg-red-500 text-white py-2 px-6 rounded-lg font-semibold hover:bg-red-600 transition self-end"
                onClick={() => {
                  // Stop any active media stream
                  if (mediaStream) {
                    mediaStream.getTracks().forEach(track => track.stop());
                    setMediaStream(null);
                  }
                  setCameraMode(false);
                  setCameraStep(0);
                  setCapturedImages([]);
                  setCapturedVideo(null);
                  setPreviewCapture(null);
                  setVideoRecording(false);
                  setRecordedChunks([]);
                  setCameraFacingMode('environment');
                  // Optionally scroll to upload section
                  setTimeout(() => {
                    document.getElementById('upload-section').scrollIntoView({ behavior: 'smooth' });
                  }, 200);
                }}
              >
                Cancel Camera & Use File Upload
              </button>
              {cameraStep < 10 ? (
                <>
                  <p className="mb-4 text-lg font-semibold text-gray-800">{cameraInstructions[cameraStep]}</p>
                  <video ref={videoRef} autoPlay playsInline className="w-full max-w-md rounded-lg border mb-4" />
                  <button
                    className="bg-gray-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-700 transition mb-2"
                    onClick={handleToggleCamera}
                  >
                    {cameraFacingMode === 'environment' ? 'Switch to Front Camera' : 'Switch to Back Camera'}
                  </button>
                  {previewCapture ? (
                    <>
                      <img src={previewCapture.url} alt="Preview" className="w-full max-w-xs rounded-lg border mb-2" id="camera-photo-preview" />
                      <div className="flex gap-4">
                        <button
                          className="bg-green-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-green-700 transition"
                          onClick={acceptPreviewCapture}
                        >
                          Accept Photo
                        </button>
                        <button
                          className="bg-red-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-red-700 transition"
                          onClick={retakePreviewCapture}
                        >
                          Retake
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      className="bg-green-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-green-700 transition"
                      onClick={handleCameraCapture}
                    >
                      {`Capture Photo ${cameraStep + 1}`}
                    </button>
                  )}
                  <p className="mt-2 text-gray-500">{`You have taken ${capturedImages.length} / 10 photos`}</p>
                </>
              ) : !capturedVideo ? (
                <>
                  <p className="mb-4 text-lg font-semibold text-gray-800">Take a 60-second close-up video of the dial, especially focusing on the second hand.</p>
                  <video ref={videoRecordRef} autoPlay playsInline className="w-full max-w-md rounded-lg border mb-4" />
                  <button
                    className={`bg-gray-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-700 transition mb-2`}
                    onClick={handleToggleCamera}
                  >
                    {cameraFacingMode === 'environment' ? 'Switch to Front Camera' : 'Switch to Back Camera'}
                  </button>
                  <button
                    className={`bg-green-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-green-700 transition ${videoRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={startVideoRecording}
                    disabled={videoRecording}
                  >
                    {videoRecording ? 'Recording...' : 'Start Recording Video'}
                  </button>
                  <p className="mt-2 text-gray-500">{videoRecording ? 'Recording will stop automatically after 60 seconds.' : ''}</p>
                </>
              ) : (
                <p className="text-green-700 font-semibold">All captures complete! You can now upload for authentication.</p>
              )}
            </div>
          )}
          {/* Hide Browse Pic buttons if cameraMode is active */}
          {!cameraMode && (
            <div className="grid grid-cols-2 gap-x-32 gap-y-16 mb-6">
              {[...Array(11)].map((_, index) => (
                <div
                  key={index}
                  className={`flex flex-col items-center justify-center ${index === 10 ? 'col-span-2' : [0, 2, 4, 6, 8].includes(index) ? 'transform -translate-x-12' : [1, 3, 5, 7, 9].includes(index) ? 'transform translate-x-4' : ''}`}
                >
                  {/* Add descriptive text above each button */}
                  {index === 0 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Case"</p>}
                  {index === 1 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Bexel" if any</p>}
                  {index === 2 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Crown" (The small knob on the side used to set time/date and wind the watch)</p>}
                  {index === 3 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Strap / Bracelet"</p>}
                  {index === 4 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Dial" (The main “face” of the watch showing the time)</p>}
                  {index === 5 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Sub-dials" (if any)</p>}
                  {index === 6 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Date Window"</p>}
                  {index === 7 && <p className="text-sm text-gray-700 mb-2">Take a good picture of "Caseback" (The rear of the watch case)</p>}
                  {index === 8 && <p className="text-sm text-gray-700 mb-2">Take a good picture of Clasp from outside</p>}
                  {index === 9 && <p className="text-sm text-gray-700 mb-2">Take a good picture of Clasp from inside</p>}
                  {/* Remove the video upload button for index 10 */}
                  {index !== 10 && (
                    <>
                      <label
                        htmlFor={`browse-pic-${index}`}
                        className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition cursor-pointer"
                      >
                        {t(`Browse Pic ${index + 1}`)}
                      </label>
                      <input
                        id={`browse-pic-${index}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUploads((prev) => {
                              const updatedImages = [...prev.images];
                              updatedImages[index] = file;
                              return { ...prev, images: updatedImages };
                            });
                          }
                        }}
                      />
                      {uploads.images[index] && (
                        <div className="mt-2 text-center">
                          {uploads.images[index]?.name && (
                            <p className="text-sm text-green-600">{uploads.images[index].name}</p>
                          )}
                          <button
                            onClick={() => {
                              setUploads((prev) => {
                                const updatedImages = [...prev.images];
                                updatedImages[index] = null;
                                return { ...prev, images: updatedImages };
                              });
                            }}
                            className="text-red-600 text-sm underline hover:text-red-800"
                          >
                            {t('Remove')}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col items-center justify-center col-span-2 -mt-7">
            <p className="text-sm text-gray-700 mb-2">Upload a 60-second video of the dial while the second hand is moving (if any)</p>
            <label
              htmlFor="browse-video"
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition cursor-pointer"
            >
              Browse a 60s Video
            </label>
            <input
              id="browse-video"
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setUploads((prev) => ({ ...prev, video: file }));
                }
              }}
            />
            {uploads.video && (
              <div className="mt-2 text-center">
                {uploads.video?.name && (
                  <p className="text-sm text-green-600">{uploads.video.name}</p>
                )}
                <button
                  onClick={() => {
                    setUploads((prev) => ({ ...prev, video: null }));
                  }}
                  className="text-red-600 text-sm underline hover:text-red-800"
                >
                  {t('Remove')}
                </button>
              </div>
            )}
          </div>
          <div className="mt-10 flex flex-col items-center">
            <button
              onClick={handleUpload}
              className="bg-gold text-black font-bold py-4 px-8 rounded-lg hover:bg-gold/80 transition duration-300 text-lg flex items-center"
            >
              {isUploading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  {t('Uploading...')} ({progress}%)
                </>
              ) : (
                'Upload For Authentication'
              )}
            </button>
            {uploadError && (
              <p className="text-red-500 text-sm mt-4 text-center">{uploadError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Example: get user info from context, Redux, or props
// Replace this with your actual authentication/user logic
const useUser = () => {
  // Get user info from localStorage (set by login)
  return JSON.parse(localStorage.getItem('user')) || { isAuthenticated: false, role: null };
};

export default function App() {
  const { t } = useTranslation();
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const user = useUser();

  // Allowed roles
  const allowedRoles = [
    'Admin',
    'Monthly Member (tier 2)',
    'Yearly Member (tier 3)'
  ];

  // Access control logic
  useEffect(() => {
    if (!user.isAuthenticated || !allowedRoles.includes(user.role)) {
      // Option 1: Redirect to login or home
      // navigate('/login');
      // Option 2: Show access denied message (see below)
    }
  }, [user]);

  if (!user.isAuthenticated || !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <h1 className="text-3xl font-bold mb-4 text-red-600">Access Denied</h1>
        <p className="text-lg text-gray-700 mb-6">You must be a subscribed member to access this area.</p>
        <Link to="/login" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition">Go to Login</Link>
      </div>
    );
  }

  const handleAnalysisComplete = (results) => {
    setAnalysisResults(results);
    setIsLoading(false);
  };

  return (
    <div className="App">
      <Header />
      <Hero />
      <BrandLogos />
      <UploadSection onAnalysisComplete={handleAnalysisComplete} />
      {/* ...existing code... */}
    </div>
  );
}
